if (!customElements.get('storecrm-restock-modal')) {
  customElements.define(
    'storecrm-restock-modal',
    class StorecrmRestockModal extends HTMLElement {
      constructor() {
        super();

        this.productId = String(this.dataset.productId || '');
        this.pendingKey = 'storecrm_combined_line_restock_pending';
        this.selectedChannel = 'email';
        this.previousFocus = null;
        this.previousOverflow = '';

        try {
          this.variants = JSON.parse(
            this.querySelector('[data-storecrm-variants]')?.textContent || '[]'
          );
        } catch (error) {
          this.variants = [];
          console.error('StoreCRM: バリエーション情報を読み込めませんでした。', error);
        }
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.modal = this.querySelector('.storecrm-combined__modal');
        this.panel = this.querySelector('.storecrm-combined__panel');
        this.launcher = this.querySelector('.storecrm-combined__launcher');
        this.submit = this.querySelector('.storecrm-combined__submit');
        this.submitLabel = this.submit?.querySelector('span');
        this.emailInput = this.querySelector('.storecrm-combined__email');
        this.consentInput = this.querySelector('.storecrm-combined__consent input');
        this.message = this.querySelector('.storecrm-combined__message');
        this.emailContent = this.querySelector('[data-email-content]');
        this.lineContent = this.querySelector('[data-line-content]');
        this.tabs = this.querySelectorAll('[data-channel]');

        this.launcher?.addEventListener('click', () => this.open());
        this.querySelectorAll('[data-storecrm-close]').forEach(button => {
          button.addEventListener('click', () => this.close());
        });
        this.tabs.forEach(tab => {
          tab.addEventListener('click', () => {
            this.activateChannel(tab.dataset.channel);
            this.clearMessage();
          });
        });
        this.submit?.addEventListener('click', () => this.register());
        this.addEventListener('keydown', event => this.handleKeydown(event));
        this.activateChannel('email');

        const productForm = document.getElementById(
          `product-form-${this.closest('main-product')?.dataset.section || ''}`
        ) || this.closest('main-product')?.querySelector('.product__form');
        productForm?.querySelector('[name="id"]')?.addEventListener('change', event => {
          this.selectCurrentUnavailableVariant(event.target.value);
        });

        this.hideNativeBlocks();
        [500, 1500, 3000].forEach(delay => {
          window.setTimeout(() => this.hideNativeBlocks(), delay);
        });
        window.setTimeout(() => this.completeLineRegistrationAfterReturn(), 300);
      }

      findSource(type) {
        return Array.from(
          document.querySelectorAll(`.storecrm-button[data-type="${type}"]`)
        ).find(element => String(element.dataset.productId) === this.productId);
      }

      hideSource(type) {
        const source = this.findSource(type);
        if (!source) return;

        const appBlock = source.closest('.shopify-app-block, [id^="shopify-block-"]');
        const target = appBlock || source;
        target.hidden = true;
        target.setAttribute('aria-hidden', 'true');
      }

      hideNativeBlocks() {
        this.hideSource('restock-button');
        this.hideSource('line-restock-button');
      }

      selectCurrentUnavailableVariant(variantId) {
        const variant = this.variants.find(item => String(item.id) === String(variantId));
        if (!variant) return;

        this.dataset.selectedVariantId = String(variant.id);
        this.hidden = Boolean(variant.available);
      }

      getSelectedVariantId() {
        return String(this.dataset.selectedVariantId || '');
      }

      open() {
        const currentVariantId = this.closest('main-product')
          ?.querySelector('.product__form [name="id"]')?.value;
        this.selectCurrentUnavailableVariant(currentVariantId);

        this.previousFocus = document.activeElement;
        this.previousOverflow = document.documentElement.style.overflow;
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        window.setTimeout(() => this.emailInput?.focus(), 0);
      }

      close() {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = this.previousOverflow;
        this.clearMessage();
        this.previousFocus?.focus?.();
      }

      handleKeydown(event) {
        if (!this.modal.classList.contains('is-open')) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          this.panel.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter(element => !element.hidden && !element.closest('[hidden]'));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      clearMessage() {
        if (!this.message) return;
        this.message.textContent = '';
        this.message.className = 'storecrm-combined__message';
      }

      showMessage(text, type) {
        this.message.textContent = text;
        this.message.className = `storecrm-combined__message is-${type}`;
      }

      activateChannel(channel) {
        this.selectedChannel = channel;
        this.tabs.forEach(tab => {
          const active = tab.dataset.channel === channel;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.setAttribute('tabindex', active ? '0' : '-1');
        });
        this.emailContent.hidden = channel !== 'email';
        this.lineContent.hidden = channel !== 'line';
        this.submitLabel.textContent = channel === 'email'
          ? '通知を受け取る'
          : 'LINEで通知を受け取る';
      }

      async getStoreCRM() {
        for (let count = 0; count < 100; count += 1) {
          if (window.storecrm) return window.storecrm;
          await new Promise(resolve => window.setTimeout(resolve, 100));
        }
        throw new Error(
          'StoreCRMを読み込めませんでした。商品情報にStoreCRMのメール・LINE再入荷通知ブロックを追加してください。'
        );
      }

      getRegistrationMode(crm) {
        if (typeof crm.getRestockRegistrationMode === 'function') {
          return crm.getRestockRegistrationMode(this.variants, this.getSelectedVariantId());
        }
        return window.storecrm_config?.favorite?.favoriteMode === 'variant'
          ? 'variant'
          : 'product';
      }

      async redirectToLoginWhenRequired(crm, type) {
        if (
          typeof crm.isRestockLoginRequired !== 'function' ||
          typeof crm.isLoggedInCustomer !== 'function' ||
          !crm.isRestockLoginRequired()
        ) return false;

        if (await crm.isLoggedInCustomer()) return false;

        const nativeButton = this.findSource(type)?.querySelector('.storecrm-restock');
        if (!nativeButton) {
          throw new Error('StoreCRMの再入荷通知ボタンを確認できませんでした。');
        }
        this.close();
        nativeButton.click();
        return true;
      }

      async registerEmail(crm) {
        if (!this.findSource('restock-button')) {
          throw new Error('商品情報にStoreCRMのメール再入荷通知ブロックを追加してください。');
        }
        if (await this.redirectToLoginWhenRequired(crm, 'restock-button')) {
          return 'redirected';
        }

        const email = this.emailInput.value.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          throw new Error('メールアドレスを正しく入力してください。');
        }

        let customerResult = null;
        try {
          customerResult = await crm.doMailCheck(email);
        } catch (error) {
          console.warn('StoreCRM: メール登録状況を確認できませんでした。', error);
        }

        await crm.doStoreRestock(
          this.getSelectedVariantId(),
          this.productId,
          email,
          true,
          Boolean(this.consentInput.checked),
          !customerResult?.email,
          this.getRegistrationMode(crm)
        );
        window.localStorage.setItem('storecrm_restock_email', email);
        return 'registered';
      }

      saveLinePending(crm, source) {
        const pending = {
          channel: 'line',
          product_id: this.productId,
          variant_id: this.getSelectedVariantId(),
          button_id: source?.id || '',
          pdp_url: window.location.href,
          mode: this.getRegistrationMode(crm),
          created_at: Date.now()
        };
        window.localStorage.setItem(this.pendingKey, JSON.stringify(pending));
        return pending;
      }

      getLinePending() {
        for (const key of [this.pendingKey, 'storecrm_pending_restock_intent']) {
          const value = window.localStorage.getItem(key);
          if (!value) continue;
          try {
            const pending = JSON.parse(value);
            if (pending?.channel === 'line') return pending;
          } catch (error) {
            console.warn('StoreCRM: LINE登録情報を読み取れませんでした。', error);
          }
        }
        return null;
      }

      clearLinePending() {
        window.localStorage.removeItem(this.pendingKey);
        window.localStorage.removeItem('storecrm_pending_restock_intent');
      }

      getLineProfileFromUrl() {
        const encoded = new URL(window.location.href).searchParams.get('line_profile_data');
        if (!encoded) return null;
        const profile = JSON.parse(window.atob(encoded));
        return {
          line_customer_id: profile.userId || profile.line_customer_id || '',
          display_name: profile.displayName || profile.display_name || '',
          picture_url: profile.pictureUrl || profile.picture_url || null
        };
      }

      removeLineResultFromUrl() {
        const url = new URL(window.location.href);
        ['line_profile_data', 'line_connect', 'line_login_error'].forEach(key => {
          url.searchParams.delete(key);
        });
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      async registerLine(crm) {
        const source = this.findSource('line-restock-button');
        if (!source) {
          throw new Error('商品情報にStoreCRMのLINE再入荷通知ブロックを追加してください。');
        }
        if (await this.redirectToLoginWhenRequired(crm, 'line-restock-button')) {
          return 'redirected';
        }

        let profile = null;
        try {
          profile = await crm.doGetLineProfile();
        } catch (error) {
          profile = null;
        }

        if (profile?.line_customer_id) {
          await crm.doStoreLineRestock(
            this.productId,
            this.getSelectedVariantId(),
            String(profile.line_customer_id),
            String(profile.display_name || ''),
            JSON.stringify(profile),
            this.getRegistrationMode(crm)
          );
          return 'registered';
        }

        this.saveLinePending(crm, source);
        try {
          await crm.doLineSubscribe();
        } catch (error) {
          this.clearLinePending();
          throw error;
        }
        return 'redirected';
      }

      async completeLineRegistrationAfterReturn() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('line_profile_data') || this.lineReturnProcessing) return;
        this.lineReturnProcessing = true;

        try {
          const pending = this.getLinePending();
          if (!pending) {
            throw new Error('LINE認証前の商品情報を確認できませんでした。もう一度LINEから登録してください。');
          }
          if (String(pending.product_id) !== this.productId) return;

          const profile = this.getLineProfileFromUrl();
          if (!profile?.line_customer_id) {
            throw new Error('LINEアカウント情報を取得できませんでした。');
          }
          window.sessionStorage.setItem('storecrm_line_profile', JSON.stringify(profile));
          window.localStorage.removeItem('storecrm_pending_restock_intent');

          const crm = await this.getStoreCRM();
          await crm.doStoreLineRestock(
            String(pending.product_id),
            String(pending.variant_id),
            String(profile.line_customer_id),
            String(profile.display_name || ''),
            JSON.stringify(profile),
            pending.mode === 'variant' ? 'variant' : 'product'
          );

          this.dataset.selectedVariantId = String(pending.variant_id);
          this.clearLinePending();
          this.removeLineResultFromUrl();
          this.activateChannel('line');
          this.open();
          this.showMessage('LINEの再入荷通知を登録しました。', 'success');
          crm.refreshAllRestockButtonStates?.(true);
        } catch (error) {
          console.error('StoreCRM: LINE認証後の登録に失敗しました。', error);
          this.activateChannel('line');
          this.open();
          this.showMessage(
            error?.message || 'LINEの再入荷通知登録に失敗しました。もう一度お試しください。',
            'error'
          );
        } finally {
          this.lineReturnProcessing = false;
        }
      }

      async register() {
        if (!this.getSelectedVariantId()) {
          this.showMessage('在庫切れのバリエーションがありません。', 'error');
          return;
        }

        this.submit.disabled = true;
        this.showMessage('登録しています…', 'loading');
        try {
          const crm = await this.getStoreCRM();
          const result = this.selectedChannel === 'email'
            ? await this.registerEmail(crm)
            : await this.registerLine(crm);
          if (result === 'redirected') return;

          this.showMessage(
            this.selectedChannel === 'line'
              ? 'LINEの再入荷通知を登録しました。'
              : '再入荷通知を登録しました。',
            'success'
          );
          crm.refreshAllRestockButtonStates?.(true);
        } catch (error) {
          console.error('StoreCRM combined restock:', error);
          this.showMessage(
            error?.message || '登録に失敗しました。時間をおいてもう一度お試しください。',
            'error'
          );
        } finally {
          this.submit.disabled = false;
        }
      }
    }
  );
}
